import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertAccessRoad,
  convertBuildingStandard,
  convertNegativeInfluence,
  convertPositiveInfluence,
  convertPredominantUse,
  convertPublicService,
  convertUrbanContext,
  convertUrbanInfrastructure,
} from '../../../api/characteristics/enumerations';
import { Region, SectionStatus } from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface HouseRegionSectionProps {
  formData: Region;
  sectionStatus?: SectionStatus;
}

export const HouseRegionSection = memo(function HouseRegionSection({
  formData,
  sectionStatus,
}: HouseRegionSectionProps) {
  return (
    <Grid container spacing={2} marginBottom={3}>
      <Grid item xs={12}>
        <SectionStatusBadge status={sectionStatus} />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="region-usos-predominantes"
        >
          Usos Predominantes
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {formData.uses.predominant_uses.length > 0 ? (
            formData.uses.predominant_uses.map((v) => (
              <AnswerTag key={v}>{convertPredominantUse(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.uses.predominant_uses_other && (
            <AnswerTag>{formData.uses.predominant_uses_other}</AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="region-infraestrutura-urbana"
        >
          Infraestrutura Urbana
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {formData.uses.urban_infrastructure.length > 0 ? (
            formData.uses.urban_infrastructure.map((v) => (
              <AnswerTag key={v}>{convertUrbanInfrastructure(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="region-servicos-publicos"
        >
          Serviços Públicos e Comunitários
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {formData.uses.public_services.length > 0 ? (
            formData.uses.public_services.map((v) => (
              <AnswerTag key={v}>{convertPublicService(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.uses.public_services_other && (
            <AnswerTag>{formData.uses.public_services_other}</AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="region-padrao-edificacoes"
        >
          Padrão das Edificações na Região
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Padrão
            </Typography>
            <Typography>
              {formData.uses.building_standard
                ? convertBuildingStandard(formData.uses.building_standard)
                : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={18} fontWeight={600} id="region-influencias">
          Influências
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography fontWeight={600}>Valorizantes</Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {formData.influences.positive.length > 0 ? (
            formData.influences.positive.map((v) => (
              <AnswerTag key={v}>{convertPositiveInfluence(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.influences.positive_other && (
            <AnswerTag>{formData.influences.positive_other}</AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>
      <Grid item xs={12}>
        <Typography fontWeight={600}>Desvalorizantes</Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {formData.influences.negative.length > 0 ? (
            formData.influences.negative.map((v) => (
              <AnswerTag key={v}>{convertNegativeInfluence(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.influences.negative_other && (
            <AnswerTag>{formData.influences.negative_other}</AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="region-outras-caracteristicas"
        >
          Outras características
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Via de Acesso
            </Typography>
            <Typography>
              {formData.other_characteristics.access_type
                ? convertAccessRoad(formData.other_characteristics.access_type)
                : '-'}
            </Typography>
          </Grid>
          {formData.other_characteristics.access_type_other && (
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Via de Acesso - Outros
              </Typography>
              <Typography>
                {formData.other_characteristics.access_type_other}
              </Typography>
            </Grid>
          )}
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Descreva a Via de Acesso
            </Typography>
            <Typography>
              {formData.other_characteristics.access_type_description ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Região
            </Typography>
            <Typography>
              {formData.other_characteristics.context
                ? convertUrbanContext(formData.other_characteristics.context)
                : '-'}
            </Typography>
          </Grid>
          {formData.other_characteristics.context_other && (
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Região - Outros
              </Typography>
              <Typography>
                {formData.other_characteristics.context_other}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
});
